/* eslint-disable dot-notation */
// ============================================================
// 離線打卡 retry scheduler
// ------------------------------------------------------------
// 撿 queue 裡的 pending 紀錄按 nextRetryAt 排程重送。
// 依紀錄性質分流：同步紀錄重打 POST 就地判成敗；非同步紀錄（帶 idempotencyKey）
// 改打 /enqueue（同 key 冪等），202 後交棒 firestorePunchWatcher（見 processQueueRecord）；
// 打卡機紀錄（punchKind: 'machine'）重打 punch-by-machine，成敗判定同同步紀錄。
//
// 觸發：mount / setTimeout / visibilitychange / 外部 kick。
// 並行：
// 	isScanning 防重複 + scanQueued 補跑（scan 在飛時的觸發不丟棄，結束後補一輪）
// 	多 tab race 靠 IDB atomic claim 處理
// UI feedback：
// 	成功 / 4xx → /hr 訂閱顯示 toast / modal
// 	5xx / timeout → 背景靜默 retry。
// ============================================================

import { Store } from 'redux';

import * as offlinePunchQueue from '@src/db/offlinePunchQueue';
import { LocalQueueRecord, RegularQueueRecord, MachineQueueRecord } from '@src/db/offlinePunchQueue';
import * as apiResponseCache from '@src/db/apiResponseCache';
import { postPunchInRecord, postTimeClockPunch, IPunchInRecord, TimeClockPunchPayload } from '@src/services/punchIn';
import { pushPunchInRetryOutcome } from '@src/store/actions/punchIn';
import * as selectors from '@src/store/selectors/user';
import {
	callPunchQueue, buildQueuePayload, PUNCH_API_TIMEOUT_MS,
} from '@src/utils/punch/punchQueueClient';

let currentAccountId: string | null = null;
let scheduledTimeoutId: ReturnType<typeof setTimeout> | null = null;
let scheduledWakeAt: number | null = null;
// scheduleNext 可以由 kick / retryNow 並行進入。run id 只用來判斷誰有資格宣告 idle；
// finite wakeAt 則採 min merge，舊成功查詢只能提早、不能延後已武裝的 timer。
let scheduleRunGeneration = 0;
let visibilityHandler: (() => void) | null = null;
let isScanning = false;
// scan 進行中又被觸發時記 dirty，結束後補跑一輪（對齊 watcher 的 syncQueued）。
// 沒有它：切帳號時 start(B) 的 initial scan 會被 A 的 in-flight scan 吞掉，B 的 pending 紀錄要等 visibilitychange / kick 才重送
let scanQueued = false;
let storeRef: Store | null = null;
// stop/start（含切帳號）都會換 generation，隔離 timer / storage backoff。terminal outcome 則
// dispatch 到 capture 的 store 並帶 record.accountId：跨頁不可丟結果，當前帳號過濾由 listener 負責。
let sessionGeneration = 0;

interface SchedulerSession {
	accountId: string;
	generation: number;
	store: Store;
}

function getCurrentSession(): SchedulerSession | null {
	if (!currentAccountId || !storeRef) return null;
	return { accountId: currentAccountId, generation: sessionGeneration, store: storeRef };
}

function isCurrentSession(session: SchedulerSession): boolean {
	return currentAccountId === session.accountId &&
		sessionGeneration === session.generation &&
		storeRef === session.store;
}

function isCurrentSessionIdentity(accountId: string, generation: number): boolean {
	return currentAccountId === accountId && sessionGeneration === generation;
}

function dispatchForCapturedSession(
	session: SchedulerSession,
	action: ReturnType<typeof pushPunchInRetryOutcome>,
): void {
	session.store.dispatch(action);
}

function isClaimAccountStillActive(
	session: SchedulerSession,
	claimed: LocalQueueRecord,
): boolean {
	return selectors.getUserId(session.store.getState()) === claimed.accountId;
}

// IndexedDB 暫時不可用時，若照 queue 裡已過期的 wakeAt 立刻重排，會形成 0ms scan busy loop。
// storage failure 使用 scheduler-level backoff；API 5xx 仍由每筆 record 的 retry backoff 處理，
// 不會進到這組計數。
const STORAGE_ERROR_BACKOFF_MS = [1000, 5000, 15_000, 30_000];
const MIN_AUTOMATIC_SCAN_DELAY_MS = 250;

let consecutiveStorageFailures = 0;
let automaticRetryNotBefore = 0;
// 並行 retryNow 可能在 auto scan 尾端建立新的 storage backoff；generation 防止舊 scan
// 看到自己流程正常就把較新的 failure reset 掉。
let storageFailureGeneration = 0;

function clearScheduledTimeout(): void {
	if (scheduledTimeoutId !== null) {
		clearTimeout(scheduledTimeoutId);
		scheduledTimeoutId = null;
	}
	scheduledWakeAt = null;
}

function armAutomaticTimer(
	accountId: string,
	wakeAt: number,
	generation: number,
	allowDelay = false,
): void {
	if (!isCurrentSessionIdentity(accountId, generation)) return;

	const rawDelay = wakeAt - Date.now();
	// wakeAt 已過期通常代表本輪沒有改變 IDB 狀態。至少讓出一個短間隔，避免未知的
	// no-progress 狀態把 event loop 跑滿；未到期的正常 timer 則維持原本精確時間。
	const delay = rawDelay <= 0 ? MIN_AUTOMATIC_SCAN_DELAY_MS : rawDelay;
	const effectiveWakeAt = Date.now() + delay;
	// 一般 queue/reclaim 查詢只能保留兩者較早值：A 舊查詢晚回 6h 時，不得 clear 掉
	// B 已排好的 1s timer。storage not-before 則是權威 barrier，允許刻意延後。
	if (!allowDelay && scheduledTimeoutId !== null && scheduledWakeAt != null &&
		scheduledWakeAt <= effectiveWakeAt) return;
	clearScheduledTimeout();
	scheduledWakeAt = effectiveWakeAt;
	scheduledTimeoutId = setTimeout(() => {
		scheduledTimeoutId = null;
		scheduledWakeAt = null;
		if (isCurrentSessionIdentity(accountId, generation)) {
			scan(); // 不 await：setTimeout callback 不該回 Promise
		}
	}, delay);
}

function recordStorageFailure(accountId: string, generation: number): void {
	// 舊帳號的 in-flight scan 不可污染切帳號後的新 scheduler。
	if (!isCurrentSessionIdentity(accountId, generation)) return;
	const index = Math.min(consecutiveStorageFailures, STORAGE_ERROR_BACKOFF_MS.length - 1);
	consecutiveStorageFailures += 1;
	storageFailureGeneration += 1;
	automaticRetryNotBefore = Date.now() + STORAGE_ERROR_BACKOFF_MS[index];
}

function resetStorageFailures(
	accountId: string,
	expectedStorageGeneration: number,
	sessionGenerationAtStart: number,
): void {
	if (!isCurrentSessionIdentity(accountId, sessionGenerationAtStart) ||
		storageFailureGeneration !== expectedStorageGeneration) return;
	consecutiveStorageFailures = 0;
	automaticRetryNotBefore = 0;
}

function deferAutomaticScanIfNeeded(accountId: string, generation: number): boolean {
	if (!isCurrentSessionIdentity(accountId, generation)) return true;
	if (automaticRetryNotBefore <= Date.now()) return false;
	armAutomaticTimer(accountId, automaticRetryNotBefore, generation, true);
	return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatLastError = (err: any): string => {
	const code = err?.code || '';
	const message = err?.message || '';
	const status = err?.response?.status || '';
	return `${code} ${message} ${status}`.trim().slice(0, 100);
};

// 加上 punch_datetime 後送 BE。BE 看到 punch_datetime 知道是離線補登，記成原打卡時間而非收到時間。
// deferred_reason 是當初 syncing→pending 那次失敗的歸類，給 BE 做初步歸因（不影響打卡邏輯）。
const buildRetryPayload = (record: RegularQueueRecord): IPunchInRecord => ({
	...record.payload,
	...(record.deferredReason ? { deferred_reason: record.deferredReason } : {}),
	punch_datetime: new Date(record.punchedAt ?? record.createdAt).toISOString(),
});

// 打卡機紀錄同上：BE 看到 punch_datetime 會記原打卡時間，並對延遲重送豁免 enc/counter 的時效驗證
const buildMachineRetryPayload = (record: MachineQueueRecord): TimeClockPunchPayload => ({
	...record.payload,
	...(record.deferredReason ? { deferred_reason: record.deferredReason } : {}),
	punch_datetime: new Date(record.punchedAt ?? record.createdAt).toISOString(),
});

// 成功後的保底 remove 延遲。listener 的 remove 走 getPunchInCall onSuccess，但 getPunchInEpic 是
// exhaustMap（在飛時後到的 call 整包被丟）→ 成功收尾一律另排這個 fallback timer 兜底（remove 冪等）
export const SUCCESS_REMOVE_FALLBACK_MS = 5 * 1000;

type ClaimedProcessResult = 'handled' | 'storage_failure';

async function finalizeAsyncAcceptedDurably(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.finalizeAsyncQueueAccepted>[1],
): Promise<offlinePunchQueue.FinalizeAsyncQueueAcceptedResult | null> {
	try {
		return await offlinePunchQueue.finalizeAsyncQueueAccepted(recordId, options);
	} catch {
		try {
			const retried = await offlinePunchQueue.finalizeAsyncQueueAccepted(recordId, options);
			offlinePunchQueue.notifyQueueSubscribers();
			return retried;
		} catch {
			const latest = await offlinePunchQueue.getById(recordId)['catch'](() => undefined);
			if (!offlinePunchQueue.isAsyncQueueAcceptedApplied(latest, options)) return null;
			offlinePunchQueue.notifyQueueSubscribers();
			return 'accepted';
		}
	}
}

async function finalizeAsyncRetryFailureDurably(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.finalizeAsyncQueueRetryFailure>[1],
): Promise<offlinePunchQueue.FinalizeAsyncQueueRetryFailureResult | null> {
	try {
		return await offlinePunchQueue.finalizeAsyncQueueRetryFailure(recordId, options);
	} catch {
		try {
			const retried = await offlinePunchQueue.finalizeAsyncQueueRetryFailure(recordId, options);
			offlinePunchQueue.notifyQueueSubscribers();
			return retried;
		} catch {
			const latest = await offlinePunchQueue.getById(recordId)['catch'](() => undefined);
			const applied = offlinePunchQueue.getAppliedAsyncQueueRetryFailureResult(latest, options);
			if (!applied) return null;
			offlinePunchQueue.notifyQueueSubscribers();
			return applied;
		}
	}
}

async function finalizeAsyncFatalDurably(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.finalizeAsyncQueueFatal>[1],
): Promise<Awaited<ReturnType<typeof offlinePunchQueue.finalizeAsyncQueueFatal>> | null> {
	try {
		return await offlinePunchQueue.finalizeAsyncQueueFatal(recordId, options);
	} catch {
		try {
			const retried = await offlinePunchQueue.finalizeAsyncQueueFatal(recordId, options);
			offlinePunchQueue.notifyQueueSubscribers();
			// not_found 沒有 tombstone 可證明是本 callback 刪除；不可推 stale fatal outcome。
			return retried;
		} catch {
			try {
				const latest = await offlinePunchQueue.getById(recordId);
				if (!latest) offlinePunchQueue.notifyQueueSubscribers();
				return null;
			} catch {
				return null;
			}
		}
	}
}

// 非同步打卡（有 idempotencyKey）的 local queue 重送：改打 /enqueue，拿到 202（含重複 key 的冪等重放，回既有 document_id）後交給 firestorePunchWatcher 收尾，這裡不判成功 / 失敗。
async function processQueueRecord(
	claimed: LocalQueueRecord,
	session: SchedulerSession,
): Promise<ClaimedProcessResult> {
	const punchedAtMs = claimed.punchedAt ?? claimed.createdAt;
	const source = {
		idempotencyKey: claimed.idempotencyKey as string,
		retryCount: claimed.retryCount,
		claimId: claimed.asyncRetryClaim?.claimId as string,
	};
	const result = await callPunchQueue(
		buildQueuePayload(claimed.payload, punchedAtMs, claimed.deferredReason),
		claimed.idempotencyKey as string,
		{
			timeout: PUNCH_API_TIMEOUT_MS,
			suppressErrorToast: true,
		},
		claimed.punchKind,
	);

	if (result.kind === 'accepted') {
		// 交棒 watcher：status 留 syncing（不再被 scheduler 撿），asyncStatus 設 accepted 讓 getOutstandingAsync 撈到
		// enqueue response 不回 status，一律設 accepted；真實狀態由 Firestore 監聽更新
		const transition = await finalizeAsyncAcceptedDurably(claimed.id, {
			source,
			docKey: result.task.document_id,
		});
		return transition === null ? 'storage_failure' : 'handled';
	}

	if (result.kind === 'retryable') {
		const transition = await finalizeAsyncRetryFailureDurably(claimed.id, {
			source,
			lastError: result.deferredReason,
		});
		if (transition === 'exhausted') {
			dispatchForCapturedSession(session, pushPunchInRetryOutcome({
				id: claimed.id,
				accountId: claimed.accountId,
				success: false,
				payload: claimed.punchKind === 'machine' ? undefined : claimed.payload,
				...(claimed.punchKind === 'machine' ? { punchKind: 'machine' as const } : {}),
				allowModal: false,
			}));
		}
		return transition === null ? 'storage_failure' : 'handled';
	}

	// fatal（其他 4xx）→ source-guarded delete；stale callback 不刪下一輪 owner。
	const transition = await finalizeAsyncFatalDurably(claimed.id, { source });
	if (transition === 'removed') {
		dispatchForCapturedSession(session, pushPunchInRetryOutcome({
			id: claimed.id,
			accountId: claimed.accountId,
			success: false,
			payload: claimed.punchKind === 'machine' ? undefined : claimed.payload,
			...(claimed.punchKind === 'machine' ? { punchKind: 'machine' as const } : {}),
			allowModal: false,
			response: result.response,
		}));
	}
	return transition === null ? 'storage_failure' : 'handled';
}

// 自動與手動路徑都先在 IDB transaction 內取得當下 record，再進這支送 API。
// 手動重送不必把 syncing 強制改回 pending，避免中間被 scheduler 撿成額外一發；但產品允許
// 使用者對 syncing 紀錄強制重送，因此自動與手動請求仍可能重疊。
async function processClaimedRecord(
	claimed: LocalQueueRecord,
	session: SchedulerSession,
): Promise<ClaimedProcessResult> {
	// 帶 idempotencyKey 的一般／卡機紀錄走各自的 /enqueue；無 key 的舊紀錄沿用同步端點。
	if (claimed.idempotencyKey) {
		// callPunchQueue 自己完成 HTTP 分類。local finalizer 有獨立 retry/read-back，不能再落到
		// markFailedRetry：accepted completion reject 會變 pending+doc hybrid，retry completion
		// reject 則會把 retryCount 加兩次。
		return processQueueRecord(claimed, session);
	}

	const outcomePayload = claimed.punchKind === 'machine' ? undefined : claimed.payload;

	try {
		// retry 走背景靜默：成功 / 4xx 由 pushPunchInRetryOutcome → usePunchInRetryOutcomeListener 顯示 toast；
		// 5xx / timeout 不該透過 axios handleCommonError 打擾使用者，這裡關掉預設 toast。
		if (claimed.punchKind === 'machine') {
			await postTimeClockPunch(buildMachineRetryPayload(claimed));
		} else {
			await postPunchInRecord(buildRetryPayload(claimed), { suppressErrorToast: true });
		}
		dispatchForCapturedSession(session, pushPunchInRetryOutcome({
			id: claimed.id,
			accountId: claimed.accountId,
			success: true,
			payload: outcomePayload,
		}));
		setTimeout(() => {
			offlinePunchQueue.remove(claimed.id)['catch'](() => { /* IDB 不可用或寫入失敗時略過，下個 TTL 週期會自動補上 */ });
		}, SUCCESS_REMOVE_FALLBACK_MS);
	} catch (err) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const response = (err as any)?.response;
		const status = response?.status;
		const isRetryable = !response || (typeof status === 'number' && status >= 500);

		if (isRetryable) {
			// 5xx / timeout / network → 寫回 IDB（pending 重排 / 達 MAX 轉 inactive）
			await offlinePunchQueue.markFailedRetry(claimed.id, formatLastError(err));
			if (claimed.retryCount + 1 >= offlinePunchQueue.MAX_RETRY_COUNT) {
				dispatchForCapturedSession(session, pushPunchInRetryOutcome({
					id: claimed.id,
					accountId: claimed.accountId,
					success: false,
					payload: outcomePayload,
				}));
			}
		} else {
			// 4xx → 刪掉 + 通知 /hr 開對應 modal / toast
			await offlinePunchQueue.remove(claimed.id);
			dispatchForCapturedSession(session, pushPunchInRetryOutcome({
				id: claimed.id,
				accountId: claimed.accountId,
				success: false,
				payload: outcomePayload,
				response: { status, data: response?.data },
			}));
		}
	}
	return 'handled';
}

async function processRecord(
	record: LocalQueueRecord,
	session: SchedulerSession,
): Promise<ClaimedProcessResult> {
	const claimed = await offlinePunchQueue.claimForRetry(record.id);
	if (!claimed) return 'handled'; // 已被自動／手動路徑或別的 tab claim
	// claim 等待期間可能切帳；API client 會讀當下 token，絕不能用 B token 送 A payload。
	// route leave / scheduler stop 但 store 仍是同一帳號則允許完成。
	if (!isClaimAccountStillActive(session, claimed)) return 'handled';
	if (claimed.punchKind === 'machine' && claimed.idempotencyKey) {
		launchMachineRetry(claimed, session);
		return 'handled';
	}
	return processClaimedRecord(claimed, session);
}

// Ownership is already durable. Background machine requests must not hold the UI or the scan open.
function launchMachineRetry(claimed: MachineQueueRecord, session: SchedulerSession): void {
	const finish = (storageFailed: boolean) => {
		if (!isCurrentSession(session)) return;
		if (storageFailed) recordStorageFailure(session.accountId, session.generation);
		kick();
	};
	processClaimedRecord(claimed, session).then(
		result => finish(result === 'storage_failure'),
		() => finish(true),
	);
}

async function scheduleNext(accountId: string, generation: number): Promise<boolean> {
	if (!isCurrentSessionIdentity(accountId, generation)) return false; // 中途登出 / 切帳號就不再排

	// storage backoff 期間不再讀 IDB，也不讓 kick / visibilitychange 把 timer 提前。
	if (deferAutomaticScanIfNeeded(accountId, generation)) return true;
	const scheduleRun = scheduleRunGeneration + 1;
	scheduleRunGeneration = scheduleRun;

	// 喚醒時刻取兩者較早：最近的 pending 重送時間、最早的 stale-syncing 回收到期時間。
	// 後者讓前景一直停留頁面時，卡在 syncing 的紀錄也能在門檻到期後被自動撿進 retry 準備區（門檻判定含 1s 誤差容忍，timer 略早醒也不空轉）
	try {
		const next = await offlinePunchQueue.getNextPending(accountId);
		const reclaimAt = await offlinePunchQueue.getNextReclaimAt(accountId);
		if (!isCurrentSessionIdentity(accountId, generation)) return false;
		// 多個 kick 可在 isScanning=false 時同時進入 scheduleNext。較早那輪若在上面 await
		// 期間發現 storage error，會建立新的 not-before；本輪必須在 arm 前重讀，不能用稍早
		// 取得的 expired wakeAt 清掉 1s/5s/... backoff，改排成 250ms hot loop。
		if (deferAutomaticScanIfNeeded(accountId, generation)) return true;
		const wakeAt = Math.min(next?.nextRetryAt ?? Infinity, reclaimAt ?? Infinity);
		if (Number.isFinite(wakeAt)) armAutomaticTimer(accountId, wakeAt, generation);
		else if (scheduleRun === scheduleRunGeneration) clearScheduledTimeout();
		return true;
	} catch (e) {
		// 連「下一次何時醒」都讀不到時，仍需靠 storage backoff 自我恢復，不能等外部 kick。
		recordStorageFailure(accountId, generation);
		if (isCurrentSessionIdentity(accountId, generation)) {
			armAutomaticTimer(accountId, automaticRetryNotBefore, generation, true);
		}
		return false;
	}
}

async function scan(): Promise<void> {
	const session = getCurrentSession();
	if (!session) return;
	if (isScanning) {
		scanQueued = true;
		return;
	}
	if (deferAutomaticScanIfNeeded(session.accountId, session.generation)) return;
	isScanning = true;
	const { accountId } = session;
	const storageGenerationAtScanStart = storageFailureGeneration;
	let hadStorageFailure = false;

	try {
		// 順手清掉 7 天前的紀錄（包含 inactive 與 syncing），順便清 API 回應快取裡過期的條目
		await offlinePunchQueue.purgeExpired(Date.now());
		await apiResponseCache.purgeExpired(Date.now());

		// visibility timeout：enqueue 在途中 tab 被關而卡在 syncing 的非同步紀錄，拉回 pending 讓下面這輪直接重送
		const reclaimResult = await offlinePunchQueue.reclaimStaleSyncing(accountId, Date.now());
		// summary 將逐筆 transaction reject 與正常 guard skipped 分開，讓 scheduler
		// 能對真正的 storage failure 啟動 backoff。
		if (reclaimResult.writeFailed > 0) hadStorageFailure = true;

		const ready = await offlinePunchQueue.listReadyForRetry(accountId, Date.now());
		await ready.reduce(async (prev, record) => {
			await prev;
			if (!isCurrentSession(session)) return; // 中途登出 / 切帳號 / restart → 略過
			const result = await processRecord(record, session);
			if (result === 'storage_failure') {
				hadStorageFailure = true;
				kick();
			}
		}, Promise.resolve());
	} catch (e) {
		// 任一步驟的 IDB 失敗都不該斷掉排程鏈：記下 storage failure，下面依 backoff reschedule。
		// scan() 一律 fire-and-forget 呼叫（timer / visibilitychange / start / kick），少了這個 catch 還會變 unhandled rejection
		hadStorageFailure = true;
	} finally {
		if (hadStorageFailure) recordStorageFailure(accountId, session.generation);
		// 仍在 isScanning = true 期間 reschedule：避免 scheduleNext await IDB 時有第二輪 scan 併行進來
		const scheduleSucceeded = await scheduleNext(accountId, session.generation);
		// 只有 scan 所有步驟與排程查詢都成功，才代表 storage 已恢復；API 5xx 在
		// processClaimedRecord 內正常落 record backoff，不會把 hadStorageFailure 設成 true。
		if (!hadStorageFailure && scheduleSucceeded) {
			resetStorageFailures(accountId, storageGenerationAtScanStart, session.generation);
		}
		isScanning = false;
		if (scanQueued) {
			scanQueued = false;
			scan();
		}
	}
}

// 同 accountId 重複呼叫 no-op；不同 accountId 會 stop + start。
export function start(accountId: string, store: Store): void {
	if (currentAccountId === accountId && storeRef === store) return;
	stop();

	currentAccountId = accountId;
	storeRef = store;

	visibilityHandler = () => {
		if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
			scan();
		}
	};
	if (typeof document !== 'undefined') {
		document.addEventListener('visibilitychange', visibilityHandler);
	}

	scan();
}

export function stop(): void {
	// 先 invalidate 所有 in-flight continuation；即使下一次登入碰巧是同 account/store，
	// 舊 session 的 outcome 也不能穿越 stop/start 邊界。
	sessionGeneration += 1;
	clearScheduledTimeout();
	if (visibilityHandler && typeof document !== 'undefined') {
		document.removeEventListener('visibilitychange', visibilityHandler);
	}
	visibilityHandler = null;
	currentAccountId = null;
	storeRef = null;
	consecutiveStorageFailures = 0;
	automaticRetryNotBefore = 0;
	storageFailureGeneration = 0;
	// isScanning 不重置：讓 in-flight scan 跑完自己收 flag（結尾會消化 scanQueued 補跑）
	scanQueued = false;
}

// handleClickPunch 寫入 IDB 後呼叫，讓 scheduler 重抓最早一筆排 timer。
// 不加這支：scheduler 啟動時 IDB 空 → idle → 後續新 pending 紀錄要等 visibilitychange 才被撿。
export function kick(): void {
	if (!currentAccountId) return;
	// 掃描中不重複工，但要留記號：scheduleNext 讀完 getNextPending / getNextReclaimAt 之後才寫入的紀錄
	// 不會被本輪排進 timer，交給 finally 的 scanQueued 補一輪（比自己再排一次 timer 單純）
	if (isScanning) {
		scanQueued = true;
		return;
	}
	if (deferAutomaticScanIfNeeded(currentAccountId, sessionGeneration)) return;
	scheduleNext(currentAccountId, sessionGeneration); // scheduleNext 自己處理 IDB failure並排 storage retry timer
}

// 手動上傳：pending 略過 backoff、inactive 允許人工救援、syncing 也強制再送一次，確保中斷後
// 永遠有人工逃生口。auto / manual 可能重疊是刻意接受的 trade-off；UI debounce 只防同頁連點。
export async function retryNow(id: string): Promise<void> {
	const session = getCurrentSession();
	if (!session) return;
	const claim = await offlinePunchQueue.claimForManualRetryDurably(id, session.accountId);
	if (claim.result !== 'claimed') {
		if (claim.result === 'unknown' && isCurrentSession(session)) {
			recordStorageFailure(session.accountId, session.generation);
		}
		// ambiguous commit 可能已留下有 expiry 的 syncing claim；不論 readback 結果，都要
		// 重新抓 getNextReclaimAt。unknown 會先尊重 storage backoff，不被 kick 繞過。
		if (isCurrentSession(session)) {
			kick();
			await scheduleNext(session.accountId, session.generation);
		}
		return;
	}
	if (!isClaimAccountStillActive(session, claim.record)) return;
	if (claim.record.punchKind === 'machine' && claim.record.idempotencyKey) {
		launchMachineRetry(claim.record, session);
		kick();
		return;
	}
	const result = await processClaimedRecord(claim.record, session);
	if (result === 'storage_failure' && isCurrentSession(session)) {
		recordStorageFailure(session.accountId, session.generation);
		kick();
	}
	if (isCurrentSession(session)) await scheduleNext(session.accountId, session.generation);
}

// Commit local changes before returning. A concurrent retry is rejected immediately rather than polled.
export async function deleteLocalQueue(
	id: string,
	accountId: string,
): Promise<offlinePunchQueue.LocalQueueDeleteResult | 'superseded'> {
	const session = getCurrentSession();
	if (!session || session.accountId !== accountId) return 'superseded';
	if (selectors.getUserId(session.store.getState()) !== accountId) return 'superseded';
	const result = await offlinePunchQueue.deleteLocalQueue(id, accountId);
	if (isCurrentSession(session)) kick();
	return result;
}

// Commit local changes before returning. A concurrent retry is rejected immediately rather than polled.
export async function operateMachineQueue(
	id: string,
	accountId: string,
	edit: offlinePunchQueue.MachineQueueEdit | null,
): Promise<offlinePunchQueue.MachineQueueOperationResult | 'superseded'> {
	const session = getCurrentSession();
	if (!session || session.accountId !== accountId) return 'superseded';
	if (selectors.getUserId(session.store.getState()) !== accountId) return 'superseded';
	const mutation = await offlinePunchQueue.mutateMachineQueue(id, accountId, edit);
	if (mutation.record) {
		if (!isClaimAccountStillActive(session, mutation.record)) return 'superseded';
		launchMachineRetry(mutation.record, session);
	}
	if (isCurrentSession(session)) kick();
	return mutation.result;
}
